import * as L from 'leaflet';

class Signaler {
  // Form
  private form: HTMLFormElement | null = null;
  private submitBtn: HTMLButtonElement | null = null;
  private requiredFields: NodeListOf<HTMLInputElement | HTMLTextAreaElement> | null = null;
  private locationFields: NodeListOf<HTMLInputElement> | null = null;
  private locationTouched = false;

  // Map
  private mapElement: HTMLElement | null = null;
  private mapInstance: L.Map | null = null;
  private currentMarker: L.Marker | null = null;
  private notificationElement: HTMLElement | null = null;
  private notificationTimer: number | null = null;

  private suggestionContainer: HTMLElement | null = null;
  private isGeolocating = false;

  constructor() {
    this.form = document.querySelector('[data-signaler-form]');
    if (!this.form) return;

    // Form
    this.initForm();
    this.initFileUpload();

    // Map
    this.initMap();
    this.initGeolocation();
    this.initAddressSuggestion();
  }

  // Form
  private initForm(): void {
    if (!this.form) return;
    this.submitBtn = this.form.querySelector('[data-signaler-submit]');
    this.requiredFields = this.form.querySelectorAll('input[required], textarea[required], input[aria-required="true"], textarea[aria-required="true"]');
    this.locationFields = this.form.querySelectorAll('[data-signaler-address-field], [data-signaler-geolocation]');

    if (this.submitBtn && this.requiredFields) {
      // Désactiver le bouton par défaut
      this.submitBtn.disabled = true;

      // Vérification initiale
      this.checkFormValidity();

      // Écouter les changements sur tous les champs requis
      this.requiredFields.forEach(field => {
        field.addEventListener('input', () => this.checkFormValidity());
        field.addEventListener('change', () => this.checkFormValidity());
      });

      this.locationFields?.forEach(field => {
        field.addEventListener('input', () => {
          this.locationTouched = true;
          this.checkFormValidity(true);
        });
        field.addEventListener('change', () => {
          this.locationTouched = true;
          this.checkFormValidity(true);
        });
      });

      // Désactiver le bouton à la soumission pour éviter le double envoi
      this.form?.addEventListener('submit', event => {
        const isLocationValid = this.hasValidLocation();

        if (!this.checkFormValidity(true)) {
          event.preventDefault();
          if (!isLocationValid) {
            this.form?.querySelector<HTMLInputElement>('[data-signaler-address-street]')?.focus();
          }
          return;
        }

        if (this.submitBtn) {
          this.submitBtn.disabled = true;
          this.submitBtn.classList.add('btn-loading');
        }
      });
    }
  }

  private checkFormValidity(showLocationError = false): boolean {
    if (!this.submitBtn || !this.requiredFields) return false;

    let isValid = true;

    this.requiredFields.forEach(field => {
      if (field.type === 'file') {
        // Pour les champs fichier, vérifier s'il y a au moins un fichier
        const fileInput = field as HTMLInputElement;
        if (!fileInput.files || fileInput.files.length === 0) {
          isValid = false;
        }
      } else if (field.type === 'checkbox') {
        // RGPD
        const checkboxInput = field as HTMLInputElement;
        if (!checkboxInput.checked) {
          isValid = false;
        }
      } else {
        // Pour les autres champs, vérifier que la valeur n'est pas vide
        if (!field.value.trim()) {
          isValid = false;
        }
        const minLength = field.getAttribute('minlength');
        if (minLength && field.value.trim().length < parseInt(minLength, 10)) {
          isValid = false;
        }
      }
    });

    const isLocationValid = this.hasValidLocation();
    isValid = isValid && isLocationValid;

    this.checkDescriptionHint();
    this.updateLocationError(isLocationValid, showLocationError || this.locationTouched);

    this.submitBtn.disabled = !isValid;

    return isValid;
  }

  private hasValidLocation(): boolean {
    if (!this.form) return false;

    const rue = this.form.querySelector<HTMLInputElement>('[data-signaler-address-street]')?.value.trim() || '';
    const codePostal = this.form.querySelector<HTMLInputElement>('[data-signaler-address-postal-code]')?.value.trim() || '';
    const ville = this.form.querySelector<HTMLInputElement>('[data-signaler-address-city]')?.value.trim() || '';
    const geolocalisation = this.form.querySelector<HTMLInputElement>('[data-signaler-geolocation]')?.value.trim() || '';

    const hasManualAddress = rue !== '' && (codePostal !== '' || ville !== '');
    const coords = geolocalisation.split(',').map(value => value.trim());

    if (coords.length !== 2 || coords.some(value => value === '')) {
      return hasManualAddress;
    }

    const lat = Number(coords[0]);
    const lng = Number(coords[1]);
    const hasValidCoordinates = Number.isFinite(lat)
      && Number.isFinite(lng)
      && lat >= -90
      && lat <= 90
      && lng >= -180
      && lng <= 180;

    return hasManualAddress || hasValidCoordinates;
  }

  private updateLocationError(isValid: boolean, showError: boolean): void {
    const error = this.form?.querySelector<HTMLElement>('[data-signaler-location-error]');
    const shouldShowError = showError && !isValid;

    if (error) {
      error.hidden = !shouldShowError;
    }

    this.locationFields?.forEach(field => {
      if (field.matches('[data-signaler-address-field]')) {
        if (shouldShowError) {
          field.setAttribute('aria-invalid', 'true');
        } else {
          field.removeAttribute('aria-invalid');
        }
      }
    });
  }

  private checkDescriptionHint(): void {
    const descriptionField = this.form?.querySelector('[data-signaler-description]') as HTMLTextAreaElement | null;
    const hint = this.form?.querySelector('[data-signaler-description-hint]') as HTMLElement | null;
    if (!descriptionField || !hint) return;

    const minLength = parseInt(descriptionField.getAttribute('minlength') || '0', 10);
    const currentLength = descriptionField.value.trim().length;

    hint.classList.toggle('page-signaler__textarea-hint--visible', currentLength > 0 && currentLength < minLength);
  }

  // Map
  private async initMap(): Promise<void> {
    this.mapElement = document.querySelector('[data-map-signaler]') as HTMLElement | null;
    this.notificationElement = document.querySelector('[data-map-notification]') as HTMLElement | null;

    if (!this.mapElement) return;

    try {
      const L = await import('leaflet');
      if (this.mapInstance) {
        this.mapInstance.remove();
      }

      // Initialiser la carte centrée sur Le Havre (même approche qu'archive-adresse)
      this.mapInstance = L.map(this.mapElement).setView([48.8588897, 2.320041], 12);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(this.mapInstance);

      // Permettre de cliquer sur la carte pour déplacer le marqueur
      this.mapInstance.on('click', async (e: L.LocationEvent) => {
        this.addMarker([e.latlng.lat, e.latlng.lng]);
        this.updateLocationField(e.latlng.lat, e.latlng.lng);

        // Mettre à jour les champs d'adresse quand on clique sur la carte
        await this.reverseGeocode(e.latlng.lat, e.latlng.lng);
      });

    } catch (error) {
      console.error("Erreur lors de l'initialisation de la carte :", error);
    }
  }

  private addMarker(latlng: [number, number]): void {
    if (!this.mapInstance) return;

    // Import dynamique dans une méthode async wrapper
    this.addMarkerAsync(latlng);
  }

  private async addMarkerAsync(latlng: [number, number]): Promise<void> {
    const L = await import('leaflet');

    // Supprimer le marqueur existant s'il y en a un
    if (this.currentMarker) {
      this.mapInstance.removeLayer(this.currentMarker);
    }

    // Récupérer l'URL de l'icône depuis un data-attribute ou utiliser l'icône par défaut
    const iconUrl = this.mapElement?.dataset.markerIcon || '/wp-content/themes/haropa/dist/assets/icons/marker-pin.svg';

    // Créer une icône personnalisée
    const customIcon = L.icon({
      iconUrl: iconUrl,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    });

    // Créer un nouveau marqueur avec l'icône personnalisée
    this.currentMarker = L.marker(latlng, { icon: customIcon }).addTo(this.mapInstance);
  }

  private updateLocationField(lat: number, lng: number): void {
    const geoField = this.form?.querySelector<HTMLInputElement>('[data-signaler-geolocation]');
    if (geoField) {
      geoField.value = `${lat},${lng}`;
      geoField.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  private initGeolocation(): void {
    const geolocationButton = this.form?.querySelector<HTMLButtonElement>('[data-signaler-geoloc-btn]');
    if (!geolocationButton) return;

    geolocationButton.addEventListener('click', async () => {
      if (navigator.geolocation) {
        this.isGeolocating = true;

        try {
          // On utilise une version Promesse de getCurrentPosition pour mieux gérer la fin
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          });

          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          // Mettre à jour le champ caché de géolocalisation
          this.updateLocationField(lat, lng);

          // Centrer la carte sur la position et ajouter un marqueur
          if (this.mapInstance) {
            this.mapInstance.setView([lat, lng], 16);
            this.addMarker([lat, lng]);
          }

          // Récupérer l'adresse via géocodage inverse
          await this.reverseGeocode(lat, lng);

          //console.log('Position obtenue:', { lat, lng });

          // Afficher un message de confirmation
          this.showMapNotification('success');
        } catch (error) {
          console.error('Erreur de géolocalisation:', error);
          this.showMapNotification('error');
        } finally {
          this.isGeolocating = false;
        }
      } else {
        console.error('La géolocalisation n\'est pas supportée par ce navigateur.');
        this.showMapNotification('error');
      }
    });
  }


  private showMapNotification(type: 'success' | 'error'): void {
    if (!this.notificationElement) return;

    const message = this.notificationElement.dataset[type === 'success' ? 'messageSuccess' : 'messageError'];
    const iconUrl = this.notificationElement.dataset[type === 'success' ? 'iconSuccess' : 'iconError'];

    if (this.notificationTimer) {
      clearTimeout(this.notificationTimer);
    }

    this.notificationElement.innerHTML = `
      <div class="btn btn--${type}">
        <img src="${iconUrl}" alt="" width="24" height="24">
        ${message}
      </div>
    `;

    this.notificationElement.classList.add('visible');

    this.notificationTimer = window.setTimeout(() => {
      this.notificationElement?.classList.remove('visible');
    }, 2000);
  }


  private async reverseGeocode(lat: number, lng: number): Promise<void> {
    try {

      this.isGeolocating = true;
      // Utiliser l'API Nominatim d'OpenStreetMap pour le géocodage inverse
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
      const data = await response.json();

      if (data && data.address) {
        const address = data.address;

        // Remplir les champs d'adresse
        this.fillAddressFields({
          numero: address.house_number || '',
          rue: this.buildStreetName(address),
          code_postal: address.postcode || '',
          ville: address.city || address.town || address.village || address.municipality || '',
        });
      }
    } catch (error) {
      console.error('Erreur lors du géocodage inverse:', error);
    } finally {
      this.isGeolocating = false;
    }
  }

  private buildStreetName(address: any): string {
    // Construire le nom de la rue en combinant les différents éléments
    const streetParts: string[] = [];

    if (address.road) streetParts.push(address.road);
    else if (address.pedestrian) streetParts.push(address.pedestrian);
    else if (address.footway) streetParts.push(address.footway);
    else if (address.path) streetParts.push(address.path);

    return streetParts.join(' ');
  }

  private fillAddressFields(addressData: { numero: string; rue: string; code_postal: string; ville: string }): void {
    const selectors = {
      numero: ['[data-signaler-address-number]'],
      rue: ['[data-signaler-address-street]'],
      code_postal: ['[data-signaler-address-postal-code]'],
      ville: ['[data-signaler-address-city]'],
    };

    // Remplir le champ numéro
    const numeroField = this.findField(selectors.numero) as HTMLInputElement;
    if (numeroField && addressData.numero) {
      numeroField.value = addressData.numero;
      this.triggerFieldEvents(numeroField);
    }

    // Remplir le champ rue
    const rueField = this.findField(selectors.rue) as HTMLInputElement;
    if (rueField && addressData.rue) {
      rueField.value = addressData.rue;
      this.triggerFieldEvents(rueField);
    }

    // Remplir le champ code postal
    const cpField = this.findField(selectors.code_postal) as HTMLInputElement;
    if (cpField && addressData.code_postal) {
      cpField.value = addressData.code_postal;
      this.triggerFieldEvents(cpField);
    }

    // Remplir le champ ville
    const villeField = this.findField(selectors.ville) as HTMLInputElement;
    if (villeField && addressData.ville) {
      villeField.value = addressData.ville;
      this.triggerFieldEvents(villeField);
    }

    // Déclencher la validation du formulaire après avoir rempli les champs
    setTimeout(() => {
      this.checkFormValidity();
    }, 200);
  }

  private findField(selectors: string[]): HTMLInputElement | null {
    for (const selector of selectors) {
      const field = this.form?.querySelector(selector) as HTMLInputElement;
      if (field) return field;
    }
    return null;
  }

  private triggerFieldEvents(field: HTMLInputElement): void {
    // Déclencher plusieurs événements pour s'assurer que CF7 détecte le changement
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
    field.dispatchEvent(new Event('blur', { bubbles: true }));

    // Événement spécial pour Contact Form 7
    field.dispatchEvent(new CustomEvent('wpcf7:input', { bubbles: true }));
  }


  private initFileUpload(): void {
    const fileInput = document.querySelector('[data-signaler-photos]') as HTMLInputElement;
    const fileBtn = document.querySelector('[data-signaler-photos-btn]') as HTMLButtonElement;
    const uploadLabel = document.querySelector('.page-signaler__photo-upload') as HTMLLabelElement;

    if (!fileInput || !uploadLabel || !fileBtn) return;
    const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB

    fileBtn.addEventListener('click', () => {
      // On déclenche manuellement un clic sur le champ de fichier caché
      (fileInput as HTMLInputElement).click();
    });

    // Force l'attribut multiple sur l'input files
    fileInput.setAttribute('multiple', 'multiple');
    fileInput.setAttribute('accept', 'image/jpeg,image/jpg,image/png,image/webp');

    // Créer une zone pour afficher la liste des fichiers
    const fileListContainer = document.createElement('div');
    fileListContainer.className = 'page-signaler__file-list';
    uploadLabel.parentNode?.insertBefore(fileListContainer, uploadLabel.nextSibling);

    // Variable pour stocker tous les fichiers sélectionnés
    let allFiles: File[] = [];

    fileInput.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement;
      const newFiles = target.files;

      if (!newFiles || newFiles.length === 0) {
        this.updateUploadButtonText(0);
        fileListContainer.innerHTML = '';
        return;
      }

      // Ajouter les nouveaux fichiers à la liste existante
      Array.from(newFiles).forEach(file => {
        // Vérifier si le fichier n'est pas déjà dans la liste
        const isDuplicate = allFiles.some(existingFile =>
          existingFile.name === file.name &&
          existingFile.size === file.size &&
          existingFile.lastModified === file.lastModified
        );
        if (file.size > maxSizeInBytes) {
          alert("Ce fichier est trop lourd !");
          return;
        }
        if (!isDuplicate && allFiles.length < 5) { // Limite à 5 fichiers
          allFiles.push(file);
        }
      });

      // Mettre à jour l'input avec tous les fichiers
      this.updateFileInput(fileInput, allFiles);

      // Afficher la liste des fichiers
      this.renderFileList(fileListContainer, allFiles);

      // Mettre à jour le texte du bouton upload
      this.updateUploadButtonText(allFiles.length);
    });

    // Gestion de la suppression d'images
    fileListContainer.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const removeBtn = target.closest('.page-signaler__file-remove') as HTMLButtonElement;

      if (removeBtn) {
        const index = parseInt(removeBtn.dataset.index || '0');

        // Supprimer le fichier de la liste
        allFiles.splice(index, 1);

        // Mettre à jour l'input
        this.updateFileInput(fileInput, allFiles);

        // Re-render la liste
        this.renderFileList(fileListContainer, allFiles);

        // Mettre à jour le texte du bouton
        this.updateUploadButtonText(allFiles.length);
      }
    });
  }

  private updateFileInput(input: HTMLInputElement, files: File[]): void {
    const dt = new DataTransfer();
    files.forEach(file => dt.items.add(file));
    input.files = dt.files;
  }

  private renderFileList(container: HTMLElement, files: File[]): void {
    container.innerHTML = '';

    files.forEach((file, index) => {
      const listItem = document.createElement('div');
      listItem.className = 'page-signaler__file-item';

      listItem.innerHTML = `
        <div class="page-signaler__file-info">
          <span class="page-signaler__file-name">${file.name}</span>
          <span class="page-signaler__file-size">${this.formatFileSize(file.size)}</span>
        </div>
        <button type="button" class="page-signaler__file-remove" data-index="${index}">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      `;

      container.appendChild(listItem);
    });
  }

  private updateUploadButtonText(count: number): void {
    const uploadText = document.querySelector('[data-signaler-photos-text]') as HTMLElement;
    if (!uploadText) return;
    const messages = {
      init: uploadText.dataset.init ?? 'Prendre ou choisir une photo',
      one: uploadText.dataset.one ?? ' photo sélectionnée',
      several: uploadText.dataset.several ?? 'photos sélectionnées'
    };

    if (count === 0) {
      uploadText.textContent = messages.init;
    } else if (count === 1) {
      uploadText.textContent = `1 ${messages.one}`;
    } else {
      uploadText.textContent = `${count} ${messages.several}`;
    }
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Suggestion adresse
  private initAddressSuggestion(): void {
    const rueInput = this.form?.querySelector('[data-signaler-address-street]') as HTMLInputElement;
    if (!rueInput) return;

    // Crée le conteneur de suggestions juste après le champ "rue"
    this.suggestionContainer = document.createElement('div');
    this.suggestionContainer.className = 'page-signaler__address-suggestions';
    this.suggestionContainer.style.width = rueInput.offsetWidth + 'px';
    this.suggestionContainer.style.display = 'none';

    // Positionne le container sous le champ "rue"
    rueInput.style.position = 'relative';
    rueInput.parentNode?.insertBefore(this.suggestionContainer, rueInput.nextSibling);

    let debounceTimer: number | undefined;

    rueInput.addEventListener('input', (e) => {
      const value = rueInput.value.trim();
      if (this.isGeolocating) {
        return;
      }
      if (debounceTimer) window.clearTimeout(debounceTimer);
      if (value.length < 3) {
        if (this.suggestionContainer) this.suggestionContainer.style.display = 'none';
        return;
      }
      debounceTimer = window.setTimeout(() => {
        this.fetchAddressSuggestions(value, rueInput);
      }, 300);
    });

    document.addEventListener('click', (e) => {
      if (!this.suggestionContainer) return;
      if (!this.suggestionContainer.contains(e.target as Node) && e.target !== rueInput) {
        this.suggestionContainer.style.display = 'none';
      }
    });
  }

  private async fetchAddressSuggestions(query: string, rueInput?: HTMLInputElement): Promise<void> {
    try {
      const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`);
      const data = await response.json();
      if (data && data.features && data.features.length > 0) {
        this.renderAddressSuggestions(data.features, rueInput);
      } else {
        if (this.suggestionContainer) this.suggestionContainer.style.display = 'none';
      }
    } catch (error) {
      if (this.suggestionContainer) this.suggestionContainer.style.display = 'none';
    }
  }

  private renderAddressSuggestions(features: any[], rueInput?: HTMLInputElement): void {
    if (!this.suggestionContainer) return;
    this.suggestionContainer.innerHTML = '';
    features.forEach((feature, idx) => {
      const suggestion = document.createElement('div');
      suggestion.className = 'page-signaler__address-suggestion';
      suggestion.textContent = feature.properties.label;
      suggestion.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this.selectAddressSuggestion(feature, rueInput);
        if (this.suggestionContainer) this.suggestionContainer.style.display = 'none';
      });
      if (this.suggestionContainer) this.suggestionContainer.appendChild(suggestion);
    });

    // Positionne le container juste sous le champ "rue"
    if (rueInput) {
      const rect = rueInput.getBoundingClientRect();
      this.suggestionContainer.style.left = rect.left + 'px';
      this.suggestionContainer.style.top = rect.bottom + window.scrollY + 'px';
      this.suggestionContainer.style.width = rect.width + 'px';
    }
    this.suggestionContainer.style.display = 'block';
  }

  private selectAddressSuggestion(feature: any, rueInput?: HTMLInputElement): void {
    // Remplir les champs adresse
    const numeroInput = this.form?.querySelector('[data-signaler-address-number]') as HTMLInputElement;
    const cpInput = this.form?.querySelector('[data-signaler-address-postal-code]') as HTMLInputElement;
    const villeInput = this.form?.querySelector('[data-signaler-address-city]') as HTMLInputElement;

    // Rue toujours mise à jour
    if (rueInput) rueInput.value = feature.properties.street || feature.properties.name || '';

    // Numéro : ne pas écraser si déjà rempli
    if (numeroInput && !numeroInput.value) {
      numeroInput.value = feature.properties.housenumber || '';
    }

    if (cpInput) cpInput.value = feature.properties.postcode || '';
    if (villeInput) villeInput.value = feature.properties.city || feature.properties.context || '';

    // Mettre à jour le champ géolocalisation
    this.updateLocationField(feature.geometry.coordinates[1], feature.geometry.coordinates[0]);

    // Déclencher la validation du formulaire
    this.checkFormValidity();
  }
}

export const signaler = new Signaler();
